import { useState } from 'react';
import { HelpPopover } from '../../help';

export interface InfrastructureModeData {
  infrastructureMode: 'BYOC' | 'MANAGED';
}

interface Props {
  tenantId: string;
  /** Called when the user submits a mode choice. The wizard wires this to its
   *  step-advance handler; the Settings → Infrastructure surface wires it to a
   *  PATCH /tenants/:id { infrastructureMode } save (CFG-IA.A1, #1536). */
  onStepComplete: (data: InfrastructureModeData) => void;
  savedData?: InfrastructureModeData;
  /** Label for the submit button. Wizard default: "Continue →"; settings
   *  passes e.g. "Save deployment mode". Minimal extraction mirroring
   *  QAPolicyForm so B2 can consolidate. */
  submitLabel?: string;
  /** When true (default) renders the wizard's "You can change this later in
   *  Settings → Organisation" hint. Settings (which IS that surface) passes
   *  false to suppress the now-circular hint. */
  showChangeLaterHint?: boolean;
}

type Mode = 'BYOC' | 'MANAGED';

const cardBase: React.CSSProperties = {
  flex: 1,
  minWidth: 260,
  padding: 24,
  borderRadius: 'var(--radius-lg)',
  border: '2px solid var(--border)',
  cursor: 'pointer',
  transition: 'border-color 150ms, background 150ms, box-shadow 150ms',
  position: 'relative',
};

const selectedCard: React.CSSProperties = {
  borderColor: 'var(--primary)',
  background: 'rgba(124,58,237,0.04)',
  boxShadow: '0 0 0 3px rgba(124,58,237,0.10)',
};

const badgeBase: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 'var(--radius-sm)',
  fontFamily: 'var(--font-body)',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const detailRow: React.CSSProperties = {
  display: 'flex',
  gap: 16,
  flexWrap: 'wrap',
  marginTop: 16,
  paddingTop: 12,
  borderTop: '1px solid var(--border)',
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  color: 'var(--text-secondary)',
};

// SVG icons
function CloudLockIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
      <rect x="10" y="13" width="4" height="5" rx="1" />
      <path d="M12 13v-2a1.5 1.5 0 0 0-3 0" />
    </svg>
  );
}

function RocketIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09Z" />
      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2Z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
  );
}

export default function Step2InfrastructureMode({
  onStepComplete,
  savedData,
  submitLabel = 'Continue →',
  showChangeLaterHint = true,
}: Props) {
  const [mode, setMode] = useState<Mode>(savedData?.infrastructureMode ?? 'MANAGED');

  const handleContinue = () => {
    onStepComplete({ infrastructureMode: mode });
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <h3 style={{
          fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700,
          color: 'var(--text)', margin: 0,
        }}>
          Deployment Mode
        </h3>
        <HelpPopover
          id="infrastructure-mode-tradeoff"
          variant="info"
          heading="Choosing deployment mode"
          body={
            <>
              <p style={{ margin: '0 0 8px' }}>
                This decision affects where your data lives and who controls the platform infrastructure. It&apos;s worth getting right at setup; switching later means migrating data and reconfiguring connections.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                <strong>Managed</strong> — BluePrint hosts the platform and your data. Faster to start, less operational burden, and BluePrint handles backups, scaling, and updates. Best for most teams, including most regulated workloads.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                <strong>BYOC (Bring Your Own Cloud)</strong> — you host the platform in your own cloud account. Data stays in your control plane, you manage infrastructure operations. Best when data residency requirements or internal policy mandate self-hosting.
              </p>
              <p style={{ margin: 0 }}>
                If your compliance team hasn&apos;t required BYOC explicitly, Managed is usually the right answer. BYOC adds operational responsibility that many teams underestimate.
              </p>
            </>
          }
        />
      </div>
      <p style={{
        fontFamily: 'var(--font-body)', fontSize: 14,
        color: 'var(--text-secondary)', marginBottom: 28,
      }}>
        Choose how BluePrint deploys and hosts your application infrastructure.
      </p>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        {/* Card 1 — BYOC */}
        <div
          role="radio"
          aria-checked={mode === 'BYOC'}
          tabIndex={0}
          onClick={() => setMode('BYOC')}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMode('BYOC'); } }}
          style={{ ...cardBase, ...(mode === 'BYOC' ? selectedCard : {}) }}
          data-testid="card-byoc"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ color: mode === 'BYOC' ? 'var(--primary)' : 'var(--text-secondary)' }}>
              <CloudLockIcon />
            </span>
            <span style={{
              ...badgeBase,
              color: 'var(--text-secondary)',
              background: 'rgba(0,0,0,0.06)',
            }}>
              Full Control
            </span>
          </div>
          <h4 style={{
            fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700,
            color: 'var(--text)', marginBottom: 8,
          }}>
            Bring Your Own Cloud
          </h4>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 13,
            color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0,
          }}>
            Connect your AWS, Azure, or GCP account. BluePrint&apos;s Deployment Agent
            provisions your infrastructure autonomously. Your data stays in your cloud account.
          </p>
          <div style={detailRow}>
            <span>&#9201; ~5 min setup</span>
            <span>&#128176; Your cloud bill</span>
            <span>&#128274; Full data sovereignty</span>
          </div>
        </div>

        {/* Card 2 — Managed */}
        <div
          role="radio"
          aria-checked={mode === 'MANAGED'}
          tabIndex={0}
          onClick={() => setMode('MANAGED')}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMode('MANAGED'); } }}
          style={{ ...cardBase, ...(mode === 'MANAGED' ? selectedCard : {}) }}
          data-testid="card-managed"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ color: mode === 'MANAGED' ? 'var(--primary)' : 'var(--text-secondary)' }}>
              <RocketIcon />
            </span>
            <span style={{
              ...badgeBase,
              color: '#059669',
              background: 'rgba(5,150,105,0.08)',
            }}>
              Recommended
            </span>
          </div>
          <h4 style={{
            fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700,
            color: 'var(--text)', marginBottom: 8,
          }}>
            BluePrint Managed
          </h4>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 13,
            color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0,
          }}>
            We host everything. Your application is live at
            {' '}<code style={{ fontSize: 12, color: 'var(--text)' }}>your-app.app.blueprintsdlc.com</code>{' '}
            in 60 seconds. No cloud account needed.
          </p>
          <div style={detailRow}>
            <span>&#9889; Instant setup</span>
            <span>&#10003; Included in subscription</span>
            <span>&#128200; Auto-scaling</span>
          </div>
        </div>
      </div>

      {showChangeLaterHint && (
        <p style={{
          fontFamily: 'var(--font-body)', fontSize: 12,
          color: 'var(--text-secondary)', marginBottom: 24,
        }}>
          You can change this later in Settings &rarr; Organisation
        </p>
      )}

      <button
        type="button"
        onClick={handleContinue}
        style={{
          fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600,
          padding: '10px 24px',
          background: 'var(--primary)', color: '#fff',
          border: 'none', borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          ...(showChangeLaterHint ? {} : { marginTop: 8 }),
        }}
      >
        {submitLabel}
      </button>
    </div>
  );
}
